import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { trackLead } from '../../utils/metaPixel';
import { buildLeadPayload, sendLead } from '../../utils/leadWebhook';
import type { EstimatePrefill, ServiceOption } from '../../types/content';

export interface LeadFormValues {
  name: string;
  phone: string;
  email: string;
  city: string;
  zip: string;
  service: ServiceOption | '';
  description: string;
}

export type LeadFormErrors = Partial<Record<keyof LeadFormValues, string>>;

export interface PhotoPreview {
  id: string;
  name: string;
  url: string;
}

const initialValues: LeadFormValues = {
  name: '',
  phone: '',
  email: '',
  city: '',
  zip: '',
  service: '',
  description: '',
};

const MAX_PHOTOS = 5;

export function useLeadForm(prefill: EstimatePrefill) {
  const [values, setValues] = useState<LeadFormValues>(initialValues);
  const [errors, setErrors] = useState<LeadFormErrors>({});
  const [photos, setPhotos] = useState<PhotoPreview[]>([]);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const photosRef = useRef(photos);
  photosRef.current = photos;

  useEffect(() => {
    if (!prefill.nonce) return;
    setStatus('idle');
    setValues((v) => ({
      ...v,
      service: prefill.service ?? v.service,
      description: prefill.note ?? v.description,
    }));
  }, [prefill.nonce, prefill.service, prefill.note]);

  useEffect(() => () => photosRef.current.forEach((p) => URL.revokeObjectURL(p.url)), []);

  const setField = <K extends keyof LeadFormValues>(key: K, value: LeadFormValues[K]) => {
    const next = key === 'phone' ? (formatPhone(value as string) as LeadFormValues[K]) : value;
    setValues((v) => ({ ...v, [key]: next }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const addPhotos = (files: FileList | null) => {
    if (!files) return;
    const room = MAX_PHOTOS - photos.length;
    const added = Array.from(files)
      .filter((f) => f.type.startsWith('image/'))
      .slice(0, room)
      .map((f) => ({ id: `${f.name}-${f.lastModified}-${Math.random()}`, name: f.name, url: URL.createObjectURL(f) }));
    setPhotos((p) => [...p, ...added]);
  };

  const removePhoto = (id: string) => {
    setPhotos((p) => {
      const target = p.find((x) => x.id === id);
      if (target) URL.revokeObjectURL(target.url);
      return p.filter((x) => x.id !== id);
    });
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length) {
      const first = Object.keys(found)[0];
      document.getElementById(`lead-${first}`)?.focus();
      return;
    }
    setStatus('submitting');
    setSubmitError(null);
    try {
      await sendLead(buildLeadPayload({ ...values, photoNames: photos.map((p) => p.name) }));
      trackLead({ content_name: values.service || 'Not Sure Yet', content_category: 'Pool Renovation Estimate' });
      setStatus('success');
    } catch {
      setStatus('error');
      setSubmitError("We couldn't send your request. Please try again or call us directly.");
    }
  };

  const reset = () => {
    photos.forEach((p) => URL.revokeObjectURL(p.url));
    setPhotos([]);
    setValues(initialValues);
    setErrors({});
    setSubmitError(null);
    setStatus('idle');
  };

  return { values, errors, photos, status, submitError, setField, addPhotos, removePhoto, submit, reset, maxPhotos: MAX_PHOTOS };
}

function validate(v: LeadFormValues): LeadFormErrors {
  const e: LeadFormErrors = {};
  if (!v.name.trim()) e.name = 'Please enter your name.';
  if (v.phone.replace(/\D/g, '').length !== 10) e.phone = 'Please enter a 10-digit phone number.';
  if (!/^\S+@\S+\.\S+$/.test(v.email.trim())) e.email = 'Please enter a valid email address.';
  if (!v.city.trim()) e.city = 'Please enter your city.';
  if (!/^\d{5}$/.test(v.zip.trim())) e.zip = 'Please enter a 5-digit ZIP code.';
  if (!v.service) e.service = 'Please choose a service, or pick "Not Sure Yet".';
  return e;
}

function formatPhone(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}
